import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button, Chip, Collapse, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import HomeAddressModal from "./HomeAddressModal";

const TAB = 'homeAddressDetails';

const useStyles = makeStyles(() => ({
  contentSection: {
    padding: '16px 22px',
    background: '#eeeeee',
    marginBottom: '15px',
    borderRadius: '3px',
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionHeaderTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#000',
  },
  tabActive: {
    color: '#1E69E5',
  },
  content: {
    padding: '16px 0',
  },
  former: {
    color: '#9e9e9e',
    background: '#fafafa',
  },
  formerCell: {
    color: 'inherit',
  },
  actionLink: {
    cursor: 'pointer',
    marginRight: 12,
  },
}));

const formatDate = (value) => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

// Links saved without the protocol would open a page of the admin instead of the site
const externalUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const COLUMNS = [
  'Address', 'City', 'State', 'ZIP Code', 'County', 'Date Added', 'Date Updated', 'Last Validated', 'Distance from Work',
  'Source URL', 'Address Status', 'Active From', 'Active Until', 'Date Marked Inactive', ''
];

const HomeAddressDetails = ({ contact, tab, handleTabChange, adminUserSession }) => {
  const classes = useStyles();
  const [homeAddresses, setHomeAddresses] = useState(null);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');

  const baseUrl = `/v1/admin/ria_contacts/${contact.contact_crd_id}/home_addresses`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-User-Email': adminUserSession.email,
      'X-Admin-User-Token': adminUserSession.token
    }
  };

  const loadHomeAddresses = () => {
    axios.get(baseUrl, config)
      .then((response) => setHomeAddresses(response.data.home_addresses))
      .catch(() => setError('The home addresses could not be loaded'));
  };

  useEffect(() => {
    loadHomeAddresses();
  }, [contact.contact_crd_id]);

  // A home address is never deleted: the delete action keeps it as a Former Address
  const handleDelete = (homeAddress) => {
    const confirmed = window.confirm('This address will stop being the home address of the contact and will be kept as a Former Address. Continue?');
    if (!confirmed) return;

    axios.delete(`${baseUrl}/${homeAddress.id}`, config)
      .then(loadHomeAddresses)
      .catch(() => setError('The address could not be deleted'));
  };

  const handleSaved = () => {
    setModal(null);
    loadHomeAddresses();
  };

  const renderRow = (homeAddress) => {
    const former = homeAddress.status === 'former';
    const cellClass = former ? classes.formerCell : undefined;
    return (
      <TableRow key={homeAddress.id} className={former ? classes.former : undefined}>
        <TableCell className={cellClass}>
          {homeAddress.address_1}
          {homeAddress.address_2 ? <div>{homeAddress.address_2}</div> : null}
          {former ? <Chip size='small' label='Former Address' style={{ marginTop: 4 }} /> : null}
        </TableCell>
        <TableCell className={cellClass}>{homeAddress.city}</TableCell>
        <TableCell className={cellClass}>{homeAddress.state}</TableCell>
        <TableCell className={cellClass}>{homeAddress.zipcode}</TableCell>
        <TableCell className={cellClass}>{homeAddress.county}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.date_added)}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.date_updated)}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.last_validated_at)}</TableCell>
        <TableCell className={cellClass}>{homeAddress.distance_from_work}</TableCell>
        <TableCell className={cellClass}>
          {homeAddress.source_url ? <Link href={externalUrl(homeAddress.source_url)} target='_blank' rel='noopener noreferrer'>Link</Link> : null}
        </TableCell>
        <TableCell className={cellClass}>{former ? 'Former' : 'Active'}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.active_from)}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.active_until)}</TableCell>
        <TableCell className={cellClass}>{formatDate(homeAddress.marked_inactive_at)}</TableCell>
        <TableCell>
          {former ? null : (
            <React.Fragment>
              <Link className={classes.actionLink} onClick={() => setModal({ homeAddress })}>Edit</Link>
              <Link className={classes.actionLink} onClick={() => handleDelete(homeAddress)}>Delete</Link>
            </React.Fragment>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <React.Fragment>
      <div className={classes.contentSection}>
        <div className={classes.contentSectionHeader} onClick={(event) => handleTabChange(event, TAB)}>
          <Typography variant='h5' className={`${classes.contentSectionHeaderTitle} ${tab === TAB ? classes.tabActive : ''}`}>Home Address Details</Typography>
          {tab === TAB ? <ExpandLess /> : <ExpandMore />}
        </div>
        <Collapse in={tab === TAB}>
          <div className={classes.content}>
            <Button variant='outlined' size='small' onClick={() => setModal({ homeAddress: null })}>Add</Button>
            {error ? <Typography style={{ color: '#d32f2f', marginTop: 8 }}>{error}</Typography> : null}
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((label) => (
                    <TableCell key={label || 'actions'}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {homeAddresses === null ? <TableRow><TableCell colSpan={COLUMNS.length}>Loading...</TableCell></TableRow> : null}
                {homeAddresses && homeAddresses.length === 0 ? <TableRow><TableCell colSpan={COLUMNS.length}>No home addresses</TableCell></TableRow> : null}
                {homeAddresses ? homeAddresses.map(renderRow) : null}
              </TableBody>
            </Table>
            </div>
          </div>
        </Collapse>
      </div>
      {modal ? (
        <HomeAddressModal
          contact={contact}
          homeAddress={modal.homeAddress}
          adminUserSession={adminUserSession}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      ) : null}
    </React.Fragment>
  );
};

export default HomeAddressDetails;
